import { calendarDaysBetween, formatLocalDate, parseDateValue, toLocalDateKey } from '@/utils/localDateUtils';
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Pago, TipoPago, EstadoPago, METODOS_PAGO } from '@/types';
import { Recibo } from '@/types/recibo';
import paymentService from '@/services/paymentService';
import reciboService from '@/services/reciboService';
import MainLayout from '@/components/layout/MainLayout';
import Button from '@/components/common/Button';
import Modal from '@/components/common/Modal';
import Input from '@/components/common/Input';
import RecordPartialPaymentModal from '@/components/payments/RecordPartialPaymentModal';
import ReciboDetailModal from '@/components/recibos/ReciboDetailModal';
import { useAuth } from '@/contexts/AuthContext';
import { useDialog } from '@/hooks/useDialog';
import { getPaymentStatusBadge } from '@/utils/paymentStatus';

export default function PaymentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { showAlert, showConfirm } = useDialog();
  const [payment, setPayment] = useState<Pago | null>(null);
  const [loading, setLoading] = useState(true);
  const [recibos, setRecibos] = useState<Recibo[]>([]);
  const [loadingRecibos, setLoadingRecibos] = useState(false);
  const [selectedRecibo, setSelectedRecibo] = useState<Recibo | null>(null);

  // Modal states
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [showPartialPaymentModal, setShowPartialPaymentModal] = useState(false);
  const [showModifyModal, setShowModifyModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showBulkModifyModal, setShowBulkModifyModal] = useState(false);
  const [showReciboDetailModal, setShowReciboDetailModal] = useState(false);

  // Form states
  const [recordForm, setRecordForm] = useState({
    payment_date: toLocalDateKey(new Date()),
    payment_method: '',
    notes: ''
  });
  const [modifyAmount, setModifyAmount] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [editForm, setEditForm] = useState({
    fecha_pago: '',
    monto: '',
    estado: ''
  });
  const [bulkModifyForm, setBulkModifyForm] = useState({
    monto: '',
    dia_pago: '',
    moneda: '',
    fecha_desde: '',
    fecha_hasta: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const loadPayment = async () => {
    try {
      if (!id) return;
      const res = await paymentService.getPaymentById(Number(id));
      setPayment((res as any).data || res);
    } finally {
      setLoading(false);
    }
  };

  const loadRecibos = async () => {
    if (!id) return;
    setLoadingRecibos(true);
    try {
      const res = await reciboService.getRecibosByPago(Number(id));
      // Ensure we always set an array
      const recibosData = (res as any).data || res;
      setRecibos(Array.isArray(recibosData) ? recibosData : []);
    } catch (error) {
      console.error('Error loading receipts:', error);
      setRecibos([]); // Set empty array on error
    } finally {
      setLoadingRecibos(false);
    }
  };

  useEffect(() => {
    loadPayment();
    loadRecibos();
  }, [id]);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return formatLocalDate(dateString, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return '-';
    return parseDateValue(dateString)?.toLocaleString('es-UY', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }) ?? '-';
  };

  const calculateDaysOverdue = (fechaLimite: string, fechaPago: string | null | undefined, estado: string) => {
    if (estado === 'PAGADO' || !fechaLimite) return null;

    const dueDate = parseDateValue(fechaLimite);
    const compareDate = fechaPago ? parseDateValue(fechaPago) : new Date();
    if (!dueDate || !compareDate) return null;
    return calendarDaysBetween(dueDate, compareDate);
  };

  // Handler for recording payment
  const handleRecordPayment = async () => {
    if (!id || !recordForm.payment_method) {
      showAlert({
        type: 'warning',
        title: 'Campos requeridos',
        message: 'Por favor complete los campos requeridos',
      });
      return;
    }

    setSubmitting(true);
    try {
      // Record the payment (backend automatically sets status to PAGADO)
      const recordResponse = await paymentService.recordPayment(Number(id), recordForm);
      console.log('Record payment response:', recordResponse);

      // Close modal first
      setShowRecordModal(false);

      // Small delay to ensure backend has committed the changes
      await new Promise(resolve => setTimeout(resolve, 300));

      // Force reload payment data
      setLoading(true);
      const res = await paymentService.getPaymentById(Number(id));
      const updatedPayment = (res as any).data || res;

      console.log('Payment data after reload:', {
        id: updatedPayment.id,
        estado: updatedPayment.estado,
        fecha_pago: updatedPayment.fecha_pago,
        metodo_pago: updatedPayment.metodo_pago,
        monto_pagado: updatedPayment.monto_pagado,
        updated_at: updatedPayment.updated_at
      });

      // Force state update by creating new object
      setPayment({ ...updatedPayment });
      setLoading(false);

      // Reset form
      setRecordForm({
        payment_date: toLocalDateKey(new Date()),
        payment_method: '',
        notes: ''
      });

      // Verify the payment was properly updated
      if (updatedPayment.estado !== EstadoPago.PAGADO) {
        console.error('Payment status was not updated to PAGADO. Current status:', updatedPayment.estado);
      }
    } catch (error: any) {
      console.error('Error recording payment:', error);
      showAlert({
        type: 'error',
        title: 'Error',
        message: error?.response?.data?.message || 'Error al registrar el pago',
      });
      setLoading(false);
    } finally {
      setSubmitting(false);
    }
  };

  // Handler for modifying amount
  const handleModifyAmount = async () => {
    if (!id || !modifyAmount || Number(modifyAmount) <= 0) {
      showAlert({
        type: 'warning',
        title: 'Monto inválido',
        message: 'Por favor ingrese un monto válido',
      });
      return;
    }

    setSubmitting(true);
    try {
      await paymentService.modifyPaymentAmount(Number(id), Number(modifyAmount));

      // Close modal first
      setShowModifyModal(false);
      setModifyAmount('');

      // Small delay to ensure backend has committed the changes
      await new Promise(resolve => setTimeout(resolve, 300));

      // Force reload payment data
      setLoading(true);
      const res = await paymentService.getPaymentById(Number(id));
      const updatedPayment = (res as any).data || res;

      console.log('Payment updated after modifying amount:', updatedPayment);

      // Force state update by creating new object
      setPayment({ ...updatedPayment });
      setLoading(false);
    } catch (error: any) {
      console.error('Error modifying amount:', error);
      showAlert({
        type: 'error',
        title: 'Error',
        message: error?.response?.data?.message || 'Error al modificar el monto',
      });
      setLoading(false);
    } finally {
      setSubmitting(false);
    }
  };

  // Handler for changing status
  const handleChangeStatus = async () => {
    if (!id || !newStatus) {
      showAlert({
        type: 'warning',
        title: 'Campo requerido',
        message: 'Por favor seleccione un estado',
      });
      return;
    }

    setSubmitting(true);
    try {
      await paymentService.updatePaymentStatus(Number(id), newStatus);

      // Close modal first
      setShowStatusModal(false);
      setNewStatus('');

      // Small delay to ensure backend has committed the changes
      await new Promise(resolve => setTimeout(resolve, 300));

      // Force reload payment data
      setLoading(true);
      const res = await paymentService.getPaymentById(Number(id));
      const updatedPayment = (res as any).data || res;

      console.log('Payment updated after changing status:', updatedPayment);

      // Force state update by creating new object
      setPayment({ ...updatedPayment });
      setLoading(false);
    } catch (error: any) {
      console.error('Error changing status:', error);
      showAlert({
        type: 'error',
        title: 'Error',
        message: error?.response?.data?.message || 'Error al cambiar el estado',
      });
      setLoading(false);
    } finally {
      setSubmitting(false);
    }
  };

  // Handler for editing payment
  const handleEditPayment = async () => {
    if (!id) return;

    if (editForm.monto.trim() !== '' && (!Number.isFinite(Number(editForm.monto)) || Number(editForm.monto) < 0)) {
      showAlert({ type: 'warning', title: 'Monto inválido', message: 'El monto debe ser mayor o igual a cero.' });
      return;
    }

    const updateData: any = {};
    if (editForm.fecha_pago) updateData.fecha_pago = editForm.fecha_pago;
    if (editForm.monto) updateData.monto = Number(editForm.monto);
    if (editForm.estado) updateData.estado = editForm.estado;

    if (Object.keys(updateData).length === 0) {
      showAlert({
        type: 'warning',
        title: 'Sin cambios',
        message: 'Por favor modifique al menos un campo',
      });
      return;
    }

    setSubmitting(true);
    try {
      await paymentService.updatePayment(Number(id), updateData);
      showAlert({
        type: 'success',
        title: 'Éxito',
        message: 'Pago actualizado exitosamente',
      });
      setShowEditModal(false);
      setEditForm({ fecha_pago: '', monto: '', estado: '' });

      // Reload payment data
      const res = await paymentService.getPaymentById(Number(id));
      setPayment((res as any).data || res);
    } catch (error: any) {
      showAlert({
        type: 'error',
        title: 'Error',
        message: error?.response?.data?.message || 'Error al actualizar el pago',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Handler for bulk modifying future payments
  const handleBulkModify = async () => {
    if (!id) return;

    const updateData: any = {};
    if (bulkModifyForm.monto) updateData.monto = Number(bulkModifyForm.monto);
    if (bulkModifyForm.dia_pago) updateData.dia_pago = Number(bulkModifyForm.dia_pago);
    if (bulkModifyForm.moneda) updateData.moneda = bulkModifyForm.moneda;
    if (bulkModifyForm.fecha_desde) updateData.fecha_desde = bulkModifyForm.fecha_desde;
    if (bulkModifyForm.fecha_hasta) updateData.fecha_hasta = bulkModifyForm.fecha_hasta;

    // Validate date range
    if (bulkModifyForm.fecha_desde && bulkModifyForm.fecha_hasta) {
      if (bulkModifyForm.fecha_desde > bulkModifyForm.fecha_hasta) {
        showAlert({
          type: 'warning',
          title: 'Fechas inválidas',
          message: 'La fecha "desde" debe ser anterior o igual a la fecha "hasta"',
        });
        return;
      }
    }

    if (Object.keys(updateData).length === 0 || (Object.keys(updateData).length === 2 && updateData.fecha_desde && updateData.fecha_hasta)) {
      showAlert({
        type: 'warning',
        title: 'Sin cambios',
        message: 'Por favor modifique al menos un campo (monto, día de pago o moneda)',
      });
      return;
    }

    const dateRangeText = bulkModifyForm.fecha_desde && bulkModifyForm.fecha_hasta 
      ? ` entre ${bulkModifyForm.fecha_desde} y ${bulkModifyForm.fecha_hasta}`
      : '';
    
    const confirmed = await showConfirm({
      title: 'Modificar pagos futuros',
      message: `¿Está seguro de modificar todos los pagos futuros de este tipo${dateRangeText}?`,
      type: 'warning',
      confirmText: 'Sí, modificar',
    });
    if (!confirmed) return;

    setSubmitting(true);
    try {
      const result = await paymentService.bulkModifyFuturePayments(Number(id), updateData);
      const resultData = (result as any).data || result;
      showAlert({
        type: 'success',
        title: 'Éxito',
        message: `Se actualizaron ${resultData.updated_count} pagos exitosamente`,
      });
      setShowBulkModifyModal(false);
      setBulkModifyForm({ monto: '', dia_pago: '', moneda: '', fecha_desde: '', fecha_hasta: '' });

      // Reload current payment data
      const res = await paymentService.getPaymentById(Number(id));
      setPayment((res as any).data || res);
    } catch (error: any) {
      showAlert({
        type: 'error',
        title: 'Error',
        message: error?.response?.data?.message || 'Error al modificar pagos futuros',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="p-6 max-w-4xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="h-96 bg-gray-200 rounded"></div>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (!payment) {
    return (
      <MainLayout>
        <div className="p-6 max-w-4xl mx-auto">
          <div className="text-center py-12">
            <div className="text-6xl mb-4">💰</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Pago no encontrado</h3>
            <Button onClick={() => navigate(-1)}>Volver</Button>
          </div>
        </div>
      </MainLayout>
    );
  }

  const daysOverdue = calculateDaysOverdue(payment.fecha_limite, payment.fecha_pago, payment.estado);
  const isPaid = payment.estado === 'PAGADO';
  const isOverdue = payment.estado === 'ATRASADO' || (daysOverdue !== null && daysOverdue > 0);
  const statusBadge = getPaymentStatusBadge(payment);

  return (
    <MainLayout>
      <div className="p-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold text-gray-900">Pago #{payment.id}</h1>
              {statusBadge && (
                <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-sm font-medium ${statusBadge.className}`}>
                  <span>{statusBadge.icon}</span>
                  {statusBadge.label}
                </span>
              )}
            </div>
            <p className="text-gray-600">
              {payment.tipo === TipoPago.INQUILINO ? 'Pago de Inquilino' : 'Pago a Propietario'}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => navigate(-1)}>
              Volver
            </Button>
            {!isPaid && hasPermission('payments', 'write') && (
              <Button onClick={() => payment.tipo === 'INQUILINO' && payment.inquilino_agente_retencion_irpf ? navigate('/receipts/new') : setShowRecordModal(true)}>
                Registrar Pago
              </Button>
            )}
          </div>
        </div>

        {/* Alert for Overdue */}
        {isOverdue && !isPaid && daysOverdue !== null && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <div className="text-2xl">⚠️</div>
              <div className="flex-1">
                <h3 className="font-semibold text-red-900 mb-1">Pago Atrasado</h3>
                <p className="text-red-700">
                  Este pago tiene {daysOverdue} día{daysOverdue !== 1 ? 's' : ''} de atraso desde la fecha límite.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Alert for Cancelled Contract */}
        {!!payment.contrato_cancelado && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <div className="text-2xl">📋</div>
              <div className="flex-1">
                <h3 className="font-semibold text-orange-900 mb-1">Contrato Cancelado</h3>
                <p className="text-orange-700">
                  Este pago pertenece a un contrato que ha sido cancelado. El pago se mantiene como pendiente para registro de deudas.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Main Info Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          {/* Amount Section */}
          <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
            <div className="text-center">
              <p className="text-sm font-medium text-gray-600 mb-2">Monto del Pago</p>
              <p className="text-5xl font-bold text-gray-900">
                {payment.moneda} {Number(payment.monto).toLocaleString('es-UY', { minimumFractionDigits: 2 })}
              </p>
              {payment.monto_pagado != null && payment.monto_pagado !== payment.monto && (
                <p className="text-sm text-gray-600 mt-2">
                  Monto pagado: {payment.moneda} {Number(payment.monto_pagado).toLocaleString('es-UY', { minimumFractionDigits: 2 })}
                </p>
              )}
            </div>
          </div>

          {/* Payment Details */}
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Detalles del Pago</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">Tipo de Pago</h3>
                <p className="text-gray-900 flex items-center gap-2">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    payment.tipo === TipoPago.INQUILINO ? 'bg-purple-100 text-purple-800' : 'bg-orange-100 text-orange-800'
                  }`}>
                    {payment.tipo}
                  </span>
                </p>
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">Contrato</h3>
                <button
                  onClick={() => navigate(`/contracts/${payment.contrato_id}`)}
                  className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                >
                  Ver Contrato {payment.numero_contrato || payment.contrato?.numero_contrato || `#${payment.contrato_id}`}
                </button>
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">Fecha Límite</h3>
                <p className="text-gray-900 font-medium">{formatDate(payment.fecha_limite)}</p>
              </div>

              {payment.fecha_pago && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Fecha de Pago</h3>
                  <p className="text-gray-900 font-medium">{formatDateTime(payment.fecha_pago)}</p>
                </div>
              )}

              {payment.metodo_pago && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Método de Pago</h3>
                  <p className="text-gray-900">{payment.metodo_pago}</p>
                </div>
              )}

              {!!payment.id_recibo && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">ID Recibo</h3>
                  <p className="text-gray-900 font-mono">{payment.id_recibo}</p>
                </div>
              )}

              {payment.dias_atraso !== undefined && payment.dias_atraso > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Días de Atraso</h3>
                  <p className="text-red-600 font-semibold">{payment.dias_atraso} día{payment.dias_atraso !== 1 ? 's' : ''}</p>
                </div>
              )}

              {!!payment.es_reajuste && (
                <div className="md:col-span-2">
                  <div className="flex items-center gap-2 text-blue-600">
                    <span className="text-lg">📈</span>
                    <span className="font-medium">Este pago incluye reajuste por IPC</span>
                  </div>
                </div>
              )}
            </div>

            {payment.notas && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h3 className="text-sm font-medium text-gray-500 mb-2">Notas</h3>
                <p className="text-gray-700 bg-gray-50 rounded-lg p-4">{payment.notas}</p>
              </div>
            )}
          </div>

          {/* Metadata */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
            <div className="flex justify-between items-center">
              <div>
                {!!payment.registrado_por && (
                  <span>Registrado por usuario #{payment.registrado_por}</span>
                )}
              </div>
              <div className="flex gap-4">
                {payment.created_at && (
                  <span>Creado: {formatDateTime(payment.created_at)}</span>
                )}
                {payment.updated_at && (
                  <span>Actualizado: {formatDateTime(payment.updated_at)}</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Actions Card */}
        {!isPaid && hasPermission('payments', 'write') && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Acciones</h2>
            <div className="flex flex-wrap gap-3">
              <Button onClick={() => payment.tipo === 'INQUILINO' && payment.inquilino_agente_retencion_irpf ? navigate('/receipts/new') : setShowRecordModal(true)}>
                Registrar Pago Completo
              </Button>
              <Button variant="secondary" onClick={() => payment.tipo === 'INQUILINO' && payment.inquilino_agente_retencion_irpf ? navigate('/receipts/new') : setShowPartialPaymentModal(true)}>
                Registrar Pago Parcial
              </Button>
              <Button variant="secondary" onClick={() => {
                setEditForm({
                  // Date inputs require YYYY-MM-DD, without the stored payment time.
                  fecha_pago: payment?.fecha_pago?.slice(0, 10) || '',
                  monto: payment?.monto?.toString() || '',
                  estado: payment?.estado || ''
                });
                setShowEditModal(true);
              }}>
                Editar Pago
              </Button>
              <Button variant="secondary" onClick={() => {
                setBulkModifyForm({
                  monto: payment?.monto?.toString() || '',
                  dia_pago: '',
                  moneda: payment?.moneda || '',
                  fecha_desde: '',
                  fecha_hasta: ''
                });
                setShowBulkModifyModal(true);
              }}>
                Modificar Pagos Futuros
              </Button>
              <Button variant="secondary" onClick={() => {
                setNewStatus(payment?.estado || '');
                setShowStatusModal(true);
              }}>
                Cambiar Estado
              </Button>
            </div>
          </div>
        )}

        {/* Receipts Section */}
        {recibos.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Recibos ({recibos.length})</h2>
            {loadingRecibos ? (
              <p className="text-gray-500">Cargando recibos...</p>
            ) : (
              <div className="space-y-3">
                {recibos.map((recibo) => (
                  <div key={recibo.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{recibo.numero_recibo}</p>
                      <div className="flex items-center gap-4 mt-1">
                        <p className="text-sm text-gray-600">
                          Monto: {recibo.moneda} {recibo.monto_recibo.toLocaleString('es-UY', { minimumFractionDigits: 2 })}
                        </p>
                        <p className="text-sm text-gray-600">
                          {formatLocalDate(recibo.fecha_emision)}
                        </p>
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          recibo.estado === 'ACTIVO'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {recibo.estado}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          setSelectedRecibo(recibo);
                          setShowReciboDetailModal(true);
                        }}
                      >
                        Ver Detalle
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={async () => {
                          try {
                            const blob = await reciboService.downloadReciboPDF(Number(recibo.id));
                            
                            // If blob is null, it means a new window was opened for printing
                            if (!blob) {
                              return;
                            }
                            
                            const url = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = `recibo-${recibo.numero_recibo}.pdf`;
                            link.click();
                            window.URL.revokeObjectURL(url);
                          } catch (error) {
                            console.error('Error downloading PDF:', error);
                            showAlert({
                              type: 'error',
                              title: 'Error',
                              message: 'Error al descargar el PDF del recibo',
                            });
                          }
                        }}
                      >
                        Descargar PDF
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Receipt Download (Legacy - for old payments) */}
        {isPaid && recibos.length === 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Recibo</h2>
            <div className="flex items-center gap-3">
              <div className="text-4xl">🧾</div>
              <div className="flex-1">
                <p className="font-medium text-gray-900">Descargar recibo de pago</p>
                {!!payment.id_recibo && (
                  <p className="text-sm text-gray-600">ID: {payment.id_recibo}</p>
                )}
                <p className="text-sm text-gray-500 mt-1">El recibo se generará en formato PDF</p>
              </div>
              <Button
                variant="secondary"
                onClick={async () => {
                  try {
                    const response = await paymentService.downloadReceipt(Number(id));
                    // The response is already a blob from axios
                    const url = window.URL.createObjectURL(response);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `recibo-${payment.id_recibo || 'pago-' + payment.id}.pdf`;
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(a);
                  } catch (error) {
                    console.error('Error downloading receipt:', error);
                    showAlert({
                      type: 'error',
                      title: 'Error',
                      message: 'Error al descargar el recibo. Por favor, intente nuevamente.',
                    });
                  }
                }}
              >
                Descargar PDF
              </Button>
            </div>
          </div>
        )}

        {/* Record Payment Modal */}
        <Modal
          isOpen={showRecordModal}
          onClose={() => setShowRecordModal(false)}
          title="Registrar Pago"
          size="md"
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fecha de Pago <span className="text-red-500">*</span>
              </label>
              <Input
                type="date"
                value={recordForm.payment_date}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setRecordForm({ ...recordForm, payment_date: e.target.value })
                }
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Método de Pago <span className="text-red-500">*</span>
              </label>
              <select
                value={recordForm.payment_method}
                onChange={(e) => setRecordForm({ ...recordForm, payment_method: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Seleccione un método de pago</option>
                {METODOS_PAGO.map((metodo) => (
                  <option key={metodo} value={metodo}>
                    {metodo}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notas (Opcional)
              </label>
              <textarea
                value={recordForm.notes}
                onChange={(e) => setRecordForm({ ...recordForm, notes: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="Ingrese notas adicionales sobre el pago..."
              />
            </div>

            <div className="flex gap-3 justify-end pt-4">
              <Button variant="secondary" onClick={() => setShowRecordModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleRecordPayment} disabled={submitting}>
                {submitting ? 'Guardando...' : 'Registrar Pago'}
              </Button>
            </div>
          </div>
        </Modal>

        {/* Change Status Modal */}
        <Modal
          isOpen={showStatusModal}
          onClose={() => setShowStatusModal(false)}
          title="Cambiar Estado del Pago"
          size="sm"
        >
          <div className="space-y-4">
            {statusBadge && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Estado Actual
              </label>
              <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-sm font-medium ${statusBadge.className}`}>
                <span>{statusBadge.icon}</span>
                {statusBadge.label}
              </span>
            </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nuevo Estado <span className="text-red-500">*</span>
              </label>
              <select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Seleccione un estado</option>
                <option value={EstadoPago.PENDIENTE}>⏳ Pendiente</option>
                <option value={EstadoPago.ATRASADO}>⚠️ Atrasado</option>
                <option value={EstadoPago.ADELANTADO}>⚡ Adelantado</option>
                <option value={EstadoPago.PAGADO}>✅ Pagado</option>
                <option value={EstadoPago.CANCELADO}>❌ Cancelado</option>
              </select>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                <strong>Nota:</strong> Cambiar el estado a "Pagado" no registra los detalles del pago. Use "Registrar Pago" para eso.
              </p>
            </div>

            <div className="flex gap-3 justify-end pt-4">
              <Button variant="secondary" onClick={() => setShowStatusModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleChangeStatus} disabled={submitting}>
                {submitting ? 'Guardando...' : 'Cambiar Estado'}
              </Button>
            </div>
          </div>
        </Modal>

        {/* Edit Payment Modal */}
        <Modal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          title="Editar Pago"
          size="md"
        >
          <div className="space-y-4">
            {payment?.estado === 'PAGADO' && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800 font-medium">
                  ⚠️ Advertencia: Este pago ya está marcado como PAGADO
                </p>
                <p className="text-sm text-yellow-700 mt-1">
                  Tenga cuidado al editar pagos que ya han sido registrados.
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fecha de Pago
              </label>
              <input
                type="date"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={editForm.fecha_pago}
                onChange={(e) => setEditForm({ ...editForm, fecha_pago: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Monto
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={editForm.monto}
                onChange={(e) => setEditForm({ ...editForm, monto: e.target.value })}
                placeholder="Ingrese el monto"
              />
              <p className="text-xs text-gray-500 mt-1">
                Al guardar $0, el pago quedará como Pagado, sin registrar un cobro ni emitir un recibo. No aplica a pagos con dinero aplicado o recibos activos.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Estado
              </label>
              <select
                value={editForm.monto.trim() !== '' && Number(editForm.monto) === 0 ? 'PAGADO' : editForm.estado}
                disabled={editForm.monto.trim() !== '' && Number(editForm.monto) === 0}
                onChange={(e) => setEditForm({ ...editForm, estado: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Mantener estado actual</option>
                <option value="PENDIENTE">⏳ Pendiente</option>
                <option value="ATRASADO">⚠️ Atrasado</option>
                <option value="ADELANTADO">⚡ Adelantado</option>
                <option value="PAGADO">✅ Pagado</option>
                <option value="CANCELADO">❌ Cancelado</option>
              </select>
            </div>

            <div className="flex gap-3 justify-end pt-4">
              <Button
                variant="secondary"
                onClick={() => setShowEditModal(false)}
                disabled={submitting}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleEditPayment}
                disabled={submitting}
              >
                {submitting ? 'Guardando...' : 'Actualizar Pago'}
              </Button>
            </div>
          </div>
        </Modal>

        {/* Bulk Modify Future Payments Modal */}
        <Modal
          isOpen={showBulkModifyModal}
          onClose={() => setShowBulkModifyModal(false)}
          title="Modificar Pagos Futuros"
          size="md"
        >
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800 font-medium">
                📝 Modificación masiva
              </p>
              <p className="text-sm text-blue-700 mt-1">
                Se actualizarán todos los pagos futuros del mismo tipo ({payment?.tipo}) que no estén marcados como PAGADO.
              </p>
            </div>

            {/* Date Range Section */}
            <div className="border-t pt-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">📅 Rango de Fechas (Opcional)</h4>
              <p className="text-xs text-gray-600 mb-3">
                Seleccione un rango para modificar solo los pagos entre esas fechas. Si no selecciona fechas, se modificarán TODOS los pagos futuros.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Desde
                  </label>
                  <input
                    type="date"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={bulkModifyForm.fecha_desde}
                    onChange={(e) => setBulkModifyForm({ ...bulkModifyForm, fecha_desde: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Hasta
                  </label>
                  <input
                    type="date"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={bulkModifyForm.fecha_hasta}
                    onChange={(e) => setBulkModifyForm({ ...bulkModifyForm, fecha_hasta: e.target.value })}
                  />
                </div>
              </div>
            </div>

            {/* Fields to Modify Section */}
            <div className="border-t pt-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">✏️ Campos a Modificar</h4>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nuevo Monto
                </label>
                <input
                  type="number"
                  step="0.01"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={bulkModifyForm.monto}
                  onChange={(e) => setBulkModifyForm({ ...bulkModifyForm, monto: e.target.value })}
                  placeholder="Dejar vacío para no modificar"
                />
              </div>

              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Día de Pago (1-31)
                </label>
                <input
                  type="number"
                  min="1"
                  max="31"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={bulkModifyForm.dia_pago}
                  onChange={(e) => setBulkModifyForm({ ...bulkModifyForm, dia_pago: e.target.value })}
                  placeholder="Dejar vacío para no modificar"
                />
              </div>

              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Moneda
                </label>
                <select
                  value={bulkModifyForm.moneda}
                  onChange={(e) => setBulkModifyForm({ ...bulkModifyForm, moneda: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Mantener moneda actual</option>
                  <option value="UYU">UYU (Pesos Uruguayos)</option>
                  <option value="USD">USD (Dólares)</option>
                </select>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800">
                <strong>Nota:</strong> Debe modificar al menos un campo para continuar.
              </p>
            </div>

            <div className="flex gap-3 justify-end pt-4">
              <Button
                variant="secondary"
                onClick={() => setShowBulkModifyModal(false)}
                disabled={submitting}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleBulkModify}
                disabled={submitting}
              >
                {submitting ? 'Procesando...' : 'Modificar Pagos Futuros'}
              </Button>
            </div>
          </div>
        </Modal>

        {/* Record Partial Payment Modal */}
        <RecordPartialPaymentModal
          isOpen={showPartialPaymentModal}
          onClose={() => setShowPartialPaymentModal(false)}
          payment={payment}
          onPaymentUpdated={() => {
            loadPayment();
            loadRecibos();
          }}
        />

        {/* Recibo Detail Modal */}
        <ReciboDetailModal
          isOpen={showReciboDetailModal}
          onClose={() => {
            setShowReciboDetailModal(false);
            setSelectedRecibo(null);
          }}
          recibo={selectedRecibo}
          onReciboUpdated={() => {
            loadRecibos();
            loadPayment();
          }}
        />
      </div>
    </MainLayout>
  );
}


